import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';

const LandingPage = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showTypeform, setShowTypeform] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([null, null, null, null]);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', instagram: '' });
  const [submitError, setSubmitError] = useState('');

  const questions = [
    "Are you willing to dance and let go?",
    "Are you willing to adhere to our dresscode?",
    "Will you allow the music to take control?",
    "Can you keep a secret?"
  ];

  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      const currentScroll = window.scrollY;
      setScrollProgress((currentScroll / totalScroll) * 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAnswer = (answer) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answer;
    setAnswers(newAnswers);

    if (currentQuestion < 3) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      if (newAnswers.every(a => a === 'yes')) {
        setShowContactForm(true);
      } else {
        setTimeout(() => {
          setShowTypeform(false);
          setCurrentQuestion(0);
          setAnswers([null, null, null, null]);
        }, 2000);
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!formData.instagram || formData.instagram.trim() === '') {
      alert('Instagram handle is required to proceed');
      return;
    }
    if (formData.name && formData.email && formData.phone && formData.instagram) {
      const newSubmission = {
        id: Date.now().toString(),
        ...formData,
        submittedAt: new Date().toISOString(),
      };

      try {
        if (!supabase) {
          throw new Error('Supabase client is not initialized. Restart the dev server after updating .env.local.');
        }

        const { error } = await supabase.from('submissions').insert({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          instagram: formData.instagram,
          submitted_at: newSubmission.submittedAt,
        });

        if (error) {
          throw error;
        }

        const existingSubmissions = JSON.parse(localStorage.getItem('arcana-submissions') || '[]');
        localStorage.setItem('arcana-submissions', JSON.stringify([newSubmission, ...existingSubmissions]));
      } catch (err) {
        console.error('Failed to save submission:', err);
        const message = err?.message || 'Unknown submission error';
        setSubmitError(message);
        alert(`Submission failed: ${message}`);
        return;
      }
      setShowSuccessMessage(true);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const resetTypeform = () => {
    setShowTypeform(false);
    setCurrentQuestion(0);
    setAnswers([null, null, null, null]);
    setShowContactForm(false);
    setShowSuccessMessage(false);
    setFormData({ name: '', email: '', phone: '', instagram: '' });
  };

  return (
    <div className="ciridae-style">
      <div className="progress-bar" style={{ width: `${scrollProgress}%` }}></div>

      <nav className="main-nav">
        <div className="nav-spacer"></div>
      </nav>

      {showTypeform && (
        <div className="typeform-overlay">
          <div className="typeform-container">
            <button className="typeform-close" onClick={resetTypeform}>
              <X />
            </button>

            {!showContactForm && !showSuccessMessage ? (
              <div className="typeform-content">
                <div className="question-number">
                  {currentQuestion + 1} / {questions.length}
                </div>
                <h2 className="typeform-question">{questions[currentQuestion]}</h2>
                
                <div className="typeform-answers">
                  <button 
                    className="answer-btn yes-btn"
                    onClick={() => handleAnswer('yes')}
                  >
                    Yes
                  </button>
                  <button 
                    className="answer-btn no-btn"
                    onClick={() => handleAnswer('no')}
                  >
                    No
                  </button>
                </div>

                {currentQuestion > 0 && (
                  <div className="answer-indicators">
                    {answers.slice(0, currentQuestion).map((answer, idx) => (
                      <div key={idx} className={`indicator ${answer}`}></div>
                    ))}
                  </div>
                )}
              </div>
            ) : showContactForm && !showSuccessMessage ? (
              <div className="typeform-content">
                <div className="question-number">5 / 5</div>
                <h2 className="typeform-question">Share your details to enter the circle</h2>
                
                <form onSubmit={handleFormSubmit} className="contact-form">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Your Telephone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                  <input
                    type="text"
                    name="instagram"
                    placeholder="Instagram Handle (Required)"
                    value={formData.instagram}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                  <button 
                    type="submit" 
                    className="answer-btn yes-btn submit-btn"
                    disabled={!formData.name || !formData.email || !formData.phone || !formData.instagram}
                  >
                    Submit
                  </button>
                  {submitError && <p className="admin-error-text">{submitError}</p>}
                </form>

                <div className="answer-indicators">
                  {answers.map((answer, idx) => (
                    <div key={idx} className={`indicator ${answer}`}></div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="typeform-success">
                <div className="success-icon">✓</div>
                <h2 className="success-title">Thank You</h2>
                <p className="success-message">
                  You will be contacted soon, if chosen to be in the circle. The hidden frequency recognizes those who seek.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <section className="hero-ciridae">
        <div className="hero-center">
          <div className="logo-symbol">
            <img 
              src="https://customer-assets.emergentagent.com/job_black-gold-landing/artifacts/ko23egzs_Arcana%20web%20logo.png"
              alt="ARCANA"
              className="hero-logo"
            />
          </div>
          
          <div className="hero-copy">
            <p className="hero-line-large">MEMBERS ONLY | VIP RAVE | HIDDEN IN THE SIERRA | ELECTRONIC DISCOVERY</p>
          </div>

          <Button 
            className="enter-circle-btn"
            onClick={() => setShowTypeform(true)}
          >
            APPLY FOR ACCESS
          </Button>
        </div>

        <div className="hero-footer">
          <p className="footer-text">What is hidden shall be revealed through sound</p>
          <p className="footer-copyright">© 2026 ARCANA · All mysteries reserved</p>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
